import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { QuestionPage } from "@/pages/qa/QuestionPage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  documents as docsApi,
  type CommentRow,
  type DocumentRole,
  type OutlineNode,
  type RevisionRow,
  type SectionDetail,
} from "@/lib/api";
import { useDocumentEvents } from "@/lib/useDocumentEvents";
import { roleAtLeast } from "@/lib/roles";
import { CodeAwareBody } from "@/lib/codeRenderer";
import { GetStartedPanel } from "@/components/GetStartedPanel";
import { useAuth } from "@/auth/AuthContext";

const STATUS_DOT: Record<string, string> = {
  outline: "bg-zinc-300",
  drafting: "bg-cyan-400",
  drafted: "bg-emerald-500",
  reviewed: "bg-emerald-600",
  finalized: "bg-blue-600",
};

const GLOBAL_STATUS_LABEL: Record<string, string> = {
  outlining: "Outlining",
  drafting: "Drafting",
  awaiting_humans: "Awaiting humans",
  published: "Published",
};

type MobileTab = "outline" | "section" | "meta";

export function Document() {
  const { docId } = useParams<{ docId: string }>();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { state: authState } = useAuth();
  const isAuthed = authState.status === "authenticated";
  const [selectedSection, setSelectedSectionRaw] = useState<string | null>(null);
  const [liveRevisions, setLiveRevisions] = useState<RevisionRow[]>([]);
  const [liveStatus, setLiveStatus] = useState<{ global_status: string; round_count: number } | null>(null);
  // On mobile (<md), only one rail is visible at a time. Picking a section
  // also flips to the "section" tab so the user sees what they just clicked.
  const [mobileTab, setMobileTab] = useState<MobileTab>("outline");
  const setSelectedSection = (id: string | null) => {
    setSelectedSectionRaw(id);
    if (id) setMobileTab("section");
  };

  // Document detail (members + outline) — works for anon on public docs.
  const docQuery = useQuery({
    queryKey: ["document", docId],
    queryFn: () => docsApi.get(docId!),
    enabled: !!docId,
  });

  // Activity feed is member-only; skip it entirely for anonymous viewers
  // to avoid a noisy 401 banner. The doc still renders without it.
  const activityQuery = useQuery({
    queryKey: ["activity", docId],
    queryFn: () => docsApi.activity(docId!, 50),
    enabled: !!docId && isAuthed,
  });

  // Selected section detail
  const sectionQuery = useQuery({
    queryKey: ["section", docId, selectedSection],
    queryFn: () => docsApi.section(docId!, selectedSection!),
    enabled: !!docId && !!selectedSection,
  });

  // Live updates via SSE — member-only (anonymous SSE is auth-gated).
  const { connected } = useDocumentEvents(isAuthed ? (docId ?? null) : null, {
    onRevision: (rev) => {
      setLiveRevisions((prev) => {
        // De-dupe by id
        if (prev.some((r) => r.id === rev.id)) return prev;
        return [...prev, rev].slice(-200);
      });
      // Refresh outline + section if a structural / content change happened
      const structural = ["create", "delete", "move", "split", "merge", "edit", "status", "claim", "release"].includes(rev.op);
      if (structural) {
        void qc.invalidateQueries({ queryKey: ["document", docId] });
        if (rev.section_id && rev.section_id === selectedSection) {
          void qc.invalidateQueries({ queryKey: ["section", docId, selectedSection] });
        }
      }
    },
    onStatus: setLiveStatus,
  });

  // Status-aware action mutations — declared BEFORE any conditional early
  // return so hook count is stable across renders.
  const submitMut = useMutation({
    mutationFn: (force: boolean) => docsApi.submit(docId!, force),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", docId] });
    },
  });
  const approveMut = useMutation({
    mutationFn: () => docsApi.approve(docId!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", docId] });
    },
  });
  const revokeMut = useMutation({
    mutationFn: (rationale: string) => docsApi.revoke(docId!, rationale),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", docId] });
    },
  });
  // Archive — owner/maintainer-only. Hides the doc from the public catalog
  // and from non-members. Reversible by the same role.
  const archiveMut = useMutation({
    mutationFn: (reason: string) => docsApi.archive(docId!, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", docId] });
      void qc.invalidateQueries({ queryKey: ["discover"] });
    },
  });
  const [showRevokePrompt, setShowRevokePrompt] = useState(false);

  // Auto-select the first non-root section once the outline loads
  useEffect(() => {
    if (selectedSection) return;
    const first = docQuery.data?.outline?.children?.[0]?.id;
    if (first) setSelectedSection(first);
  }, [docQuery.data, selectedSection]);

  if (!docId) return <div className="p-8">Missing document id</div>;

  if (docQuery.isLoading) {
    return <div className="p-8 text-zinc-500">Loading document…</div>;
  }
  if (docQuery.error) {
    const e = docQuery.error;
    if (e instanceof ApiError && e.status === 403) {
      return <ErrorScreen title="Access denied" body="You're not a member of this document." />;
    }
    if (e instanceof ApiError && e.status === 404) {
      return <ErrorScreen title="Not found" body="That document doesn't exist (or has been deleted)." />;
    }
    return <ErrorScreen title="Failed to load" body={String(e)} />;
  }

  const doc = docQuery.data!.document;
  const outline = docQuery.data!.outline;
  const members = docQuery.data!.members;
  const published = docQuery.data!.published;

  // Phase 2 redesign: `kind=qa` documents render the dedicated
  // Stack-Overflow-shaped page instead of the three-rail editor.
  // Escape hatch: `?write_answer=1` falls through to the editor so an
  // answering user can post a section via the existing tooling. Once
  // we have a dedicated `propose_answer` REST endpoint, this fallback
  // can go away.
  const wantsEditor = searchParams.get("write_answer") === "1";
  if (doc.kind === "qa" && !wantsEditor) {
    return <QuestionPage doc={docQuery.data!} />;
  }

  const initialRevisions = activityQuery.data?.revisions ?? [];
  const allRevisions = mergeRevisions(initialRevisions, liveRevisions);

  const status = liveStatus?.global_status ?? doc.global_status;
  const round = liveStatus?.round_count ?? activityQuery.data?.round_count ?? 0;
  const myRole = doc.role as DocumentRole;
  const isMaintainer = roleAtLeast(myRole, "maintainer");

  return (
    <div className="h-screen flex flex-col">
      {/* Top bar — wraps on mobile; hides secondary text links/round/role until md+ */}
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 sm:px-4 py-2 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Link
            to="/"
            className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 flex-shrink-0"
          >
            ←
          </Link>
          <h1 className="font-medium truncate text-sm sm:text-base">{doc.title}</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
            {GLOBAL_STATUS_LABEL[status] ?? status}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
              doc.visibility === "public"
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200"
            }`}
            title={
              doc.visibility === "public"
                ? "Listed in discovery — anyone can read"
                : "Private — only members can see this document"
            }
          >
            {doc.visibility}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 text-xs text-zinc-500 flex-shrink-0">
          {/* Download is available to anyone who can see the doc page (anon
              on public docs, members on private). Sits outside the auth guard
              below so anonymous viewers see it too. */}
          <DownloadMenu docId={docId} />
          {isAuthed && myRole !== "anonymous" && (
            <ContextualActions
              status={status}
              isMaintainer={isMaintainer}
              myRole={myRole}
              submitting={submitMut.isPending}
              approving={approveMut.isPending}
              revoking={revokeMut.isPending}
              submitError={apiErrText(submitMut.error)}
              approveError={apiErrText(approveMut.error)}
              revokeError={apiErrText(revokeMut.error)}
              onSubmit={(force) => submitMut.mutate(force)}
              onApprove={() => approveMut.mutate()}
              onAskRevoke={() => setShowRevokePrompt(true)}
              publishedUrl={published?.url ?? null}
            />
          )}
          {/* Member-only top-bar items (Sources / live / round / role)
              are hidden for anonymous viewers and on mobile (mobile users see
              them in the meta tab via the mobile tab strip below). */}
          {isAuthed && myRole !== "anonymous" && (
            <>
              <Link
                to={`/d/${docId}/sources`}
                className="hidden md:inline text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
                title="Briefs and reference materials"
              >
                Sources
              </Link>
              <span className="hidden md:inline text-zinc-300 dark:text-zinc-700">·</span>
              <span className="hidden md:inline" title="Live update connection">
                {connected ? <span className="text-emerald-600 dark:text-emerald-400">●</span> : <span className="text-zinc-400">○</span>}{" "}
                {connected ? "live" : "reconnecting…"}
              </span>
              <span className="hidden md:inline">round {round}</span>
              <span className="hidden md:inline uppercase tracking-wide">{doc.role}</span>
              {isMaintainer && (
                <button
                  type="button"
                  onClick={() => {
                    const reason = window.prompt(
                      "Archive this document?\n\nIt will vanish from /discover and 404 for non-members.\nYou can reverse this any time.\n\nOptional reason for the audit log:",
                      "",
                    );
                    if (reason === null) return; // cancelled
                    archiveMut.mutate(reason);
                  }}
                  disabled={archiveMut.isPending}
                  className="hidden md:inline text-zinc-400 hover:text-red-600 dark:hover:text-red-400 underline-offset-2 hover:underline disabled:opacity-50"
                  title="Archive — hides this doc from /discover and from non-members"
                >
                  {archiveMut.isPending ? "Archiving…" : "Archive"}
                </button>
              )}
            </>
          )}
          {/* Anonymous viewers get a sign-in CTA. Authenticated non-members
              do NOT — clicking would round-trip through /login and bounce
              back here unchanged (the SPA already considers them logged in;
              they just aren't a member of *this* doc). They contribute via
              their agent's MCP connector instead. */}
          {!isAuthed && (
            <Link
              to={`/login?from=${encodeURIComponent(`/d/${docId}`)}`}
              className="rounded-md bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 text-sm font-medium hover:opacity-90"
            >
              Sign in to contribute
            </Link>
          )}
          {isAuthed && myRole === "anonymous" && (
            <Link
              to="/tokens"
              className="rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-900"
              title="Get an agent token, then your AI agent can contribute via MCP"
            >
              Contribute via agent
            </Link>
          )}
        </div>
      </header>

      {/* Tag row (Phase 13 — Q&A). Hidden when the doc has no tags so it
          doesn't introduce empty chrome on legacy docs. */}
      {doc.tags && doc.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 sm:px-4 py-2 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
          {doc.tags.map((t) => (
            <span
              key={t.slug}
              className="text-xs px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
              title={`Tag: ${t.name}`}
            >
              {t.slug}
            </span>
          ))}
        </div>
      )}

      {/* Anonymous / non-member viewer banner — different copy per case. */}
      {!isAuthed && (
        <div className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40 px-4 py-2 text-xs text-zinc-600 dark:text-zinc-400 flex-shrink-0">
          You're viewing a public document. Sign in to comment, contribute sections, or bring your agent.
        </div>
      )}
      {isAuthed && myRole === "anonymous" && (
        <div className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40 px-4 py-2 text-xs text-zinc-600 dark:text-zinc-400 flex-shrink-0">
          You're signed in but not a member of this document. To contribute, point your AI agent at it via MCP — it'll auto-promote on first contribution. <Link to="/tokens" className="underline hover:text-zinc-900 dark:hover:text-zinc-100">Get an agent token →</Link>
        </div>
      )}

      {/* Mobile tab strip — only visible on small screens */}
      <nav className="md:hidden flex border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0 text-sm bg-white dark:bg-zinc-950">
        <MobileTabButton
          active={mobileTab === "outline"}
          onClick={() => setMobileTab("outline")}
        >
          Outline{outline ? ` (${outline.children.length})` : ""}
        </MobileTabButton>
        <MobileTabButton
          active={mobileTab === "section"}
          onClick={() => setMobileTab("section")}
          disabled={!selectedSection}
        >
          Section
        </MobileTabButton>
        <MobileTabButton
          active={mobileTab === "meta"}
          onClick={() => setMobileTab("meta")}
        >
          {isAuthed ? `Activity${connected ? "" : " (reconnecting…)"}` : "Members"}
        </MobileTabButton>
      </nav>

      {showRevokePrompt && (
        <RevokePrompt
          busy={revokeMut.isPending}
          onCancel={() => setShowRevokePrompt(false)}
          onConfirm={(rationale) => {
            revokeMut.mutate(rationale, {
              onSuccess: () => setShowRevokePrompt(false),
            });
          }}
        />
      )}

      {/* Layout: 3 rails on md+, single-pane (tabbed) on mobile */}
      <div className="flex-1 flex min-h-0">
        {/* Outline rail — always shown on md+, only when active tab=outline on mobile */}
        <aside
          className={`${mobileTab === "outline" ? "flex" : "hidden"} md:flex flex-col w-full md:w-64 border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto p-2`}
        >
          <div className="text-xs uppercase tracking-wide text-zinc-500 px-2 py-1">
            Outline
          </div>
          {outline?.children.length === 0 && (
            <div className="px-2 py-2 text-xs text-zinc-500 italic">No sections yet</div>
          )}
          <ul>
            {outline?.children.map((node) => (
              <OutlineItem
                key={node.id}
                node={node}
                depth={0}
                selected={selectedSection}
                onSelect={setSelectedSection}
              />
            ))}
          </ul>
        </aside>

        {/* Section detail — always shown on md+, only when active tab=section on mobile */}
        <main
          className={`${mobileTab === "section" ? "flex" : "hidden"} md:flex flex-col flex-1 overflow-y-auto p-4 md:p-8`}
        >
          {sectionQuery.isLoading && <div className="text-zinc-500">Loading section…</div>}
          {/* "Get started" empty-state: shown to members on a doc with zero
              activity. Auto-dismisses when the agent's first MCP call lands
              and SSE pushes a revision (activityQuery refetches → empty
              array fills → this branch flips off). */}
          {!sectionQuery.isLoading &&
            isAuthed &&
            myRole !== "anonymous" &&
            roleAtLeast(myRole, "viewer") &&
            (activityQuery.data?.revisions?.length ?? 0) === 0 &&
            liveRevisions.length === 0 &&
            // ...and the document is actually empty. An empty revision list
            // alone is a bad proxy: a doc created with content already in it
            // (compare_models) has no history yet still shows three finished
            // sections, and "Your agent hasn't started yet" over a full page
            // is nonsense.
            (outline?.children?.length ?? 0) === 0 && (
              <GetStartedPanel
                docId={docId}
                title={doc.title}
                kind={doc.kind}
                language={doc.language}
              />
            )}
          {sectionQuery.data && (
            <SectionView
              detail={sectionQuery.data}
              docId={docId}
              docTitle={doc.title}
              myRole={myRole}
              isAnswer={
                // Q&A shape means *this doc is a Q&A*, not merely "the section
                // is a direct child of root" — which is true of every section
                // in a flat document. Without the kind check, a model
                // comparison offers "Accept this answer" on each draft.
                doc.kind === "qa" &&
                (outline?.children.some((c) => c.id === sectionQuery.data.section.id) ?? false)
              }
              acceptedAnswerSectionId={docQuery.data?.accepted_answer_section_id ?? null}
              onMutated={() => {
                void qc.invalidateQueries({ queryKey: ["section", docId, selectedSection] });
                void qc.invalidateQueries({ queryKey: ["document", docId] });
              }}
            />
          )}
          {!selectedSection && !sectionQuery.isLoading &&
            !(
              isAuthed &&
              myRole !== "anonymous" &&
              (activityQuery.data?.revisions?.length ?? 0) === 0 &&
              liveRevisions.length === 0
            ) && (
            <div className="text-zinc-500">
              <span className="md:hidden">Tap a section in Outline to start.</span>
              <span className="hidden md:inline">Select a section on the left.</span>
            </div>
          )}
        </main>

        {/* Members + Activity rail — always shown on md+, only when active tab=meta on mobile.
            On mobile, also surfaces Sources since that's hidden from the top
            bar to save horizontal space. */}
        <aside
          className={`${mobileTab === "meta" ? "flex" : "hidden"} md:flex flex-col w-full md:w-80 border-l border-zinc-200 dark:border-zinc-800 overflow-y-auto p-3`}
        >
          {/* Mobile-only quick links (replace what was in the top bar). Hidden
              for anonymous viewers since those routes require membership. */}
          {isAuthed && myRole !== "anonymous" && (
            <div className="md:hidden mb-4 pb-3 border-b border-zinc-200 dark:border-zinc-800 space-y-2 text-sm">
              <Link
                to={`/d/${docId}/sources`}
                className="block text-zinc-700 dark:text-zinc-300 hover:underline"
              >
                📎 Sources & briefs
              </Link>
              <div className="text-xs text-zinc-500 pt-1">
                {connected ? (
                  <span className="text-emerald-600 dark:text-emerald-400">● live</span>
                ) : (
                  <span>○ reconnecting…</span>
                )}
                {" · "}round {round} · {doc.role}
              </div>
            </div>
          )}

          <div className="text-xs uppercase tracking-wide text-zinc-500 px-1 py-1">
            Members ({members.length})
          </div>
          <ul className="text-sm mb-4">
            {members.map((m, idx) => {
              // Redacted shape (anon / non-member) drops user_id; only role survives.
              const label = m.user_id ? `${m.user_id.slice(0, 8)}…` : "Member";
              return (
                <li
                  key={m.user_id ?? `m${idx}`}
                  className="px-1 py-1 flex items-center justify-between"
                >
                  <span
                    className="truncate text-xs text-zinc-700 dark:text-zinc-300"
                    title={m.user_id ?? undefined}
                  >
                    {label}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-zinc-500">
                    {m.role}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="text-xs uppercase tracking-wide text-zinc-500 px-1 py-1">
            Activity
          </div>
          <ActivityFeed revisions={allRevisions} />
        </aside>
      </div>
    </div>
  );
}

function MobileTabButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex-1 px-3 py-2 text-center border-b-2 transition ${
        active
          ? "border-zinc-900 dark:border-zinc-100 text-zinc-900 dark:text-zinc-100 font-medium"
          : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
      } disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

// ---- helpers --------------------------------------------------------------

function mergeRevisions(initial: RevisionRow[], live: RevisionRow[]): RevisionRow[] {
  const map = new Map<string, RevisionRow>();
  for (const r of initial) map.set(r.id, r);
  for (const r of live) map.set(r.id, r);
  return Array.from(map.values()).sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
}

function OutlineItem({
  node,
  depth,
  selected,
  onSelect,
}: {
  node: OutlineNode;
  depth: number;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const isSelected = node.id === selected;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(node.id)}
        className={`w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 hover:bg-zinc-100 dark:hover:bg-zinc-900 ${isSelected ? "bg-zinc-100 dark:bg-zinc-900 font-medium" : ""}`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[node.status] ?? "bg-zinc-300"}`} />
        <span className="truncate text-sm flex-1 min-w-0">{node.title}</span>
        {node.locked && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 font-medium flex-shrink-0 uppercase tracking-wide"
            title="Section locked by owner — only the owner can edit"
          >
            Locked
          </span>
        )}
        {node.unresolved_comment_count > 0 && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 flex-shrink-0"
            title={`${node.unresolved_comment_count} unresolved comment${node.unresolved_comment_count === 1 ? "" : "s"}`}
          >
            💬 {node.unresolved_comment_count}
          </span>
        )}
        {node.claim && (
          <span className="text-xs text-amber-600 dark:text-amber-400 flex-shrink-0" title={`Claimed by ${node.claim.party_id.slice(0, 8)}…`}>
            ✋
          </span>
        )}
      </button>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((c) => (
            <OutlineItem
              key={c.id}
              node={c}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface SectionViewData {
  section: SectionDetail;
  comments: CommentRow[];
  revisions: RevisionRow[];
}

interface SectionViewProps {
  detail: SectionViewData;
  docId: string;
  /** Used to suppress the section heading row when the section's title
   *  exactly matches the doc title — avoids the visual stutter you get
   *  when `create_document` auto-seeds a first child section named
   *  after the doc itself. The status/locked pills still render. */
  docTitle: string;
  myRole: DocumentRole;
  /** True iff this section is a direct child of `outline_root` — i.e. an
   *  answer in Q&A shape. Drives the "Accept this answer" affordance. */
  isAnswer: boolean;
  /** Pointer to the currently-accepted answer (or null). Drives the
   *  emerald "✓ Accepted answer" pill on the matching section. */
  acceptedAnswerSectionId: string | null;
  onMutated: () => void;
}

function SectionView({
  detail,
  docId,
  docTitle,
  myRole,
  isAnswer,
  acceptedAnswerSectionId,
  onMutated,
}: SectionViewProps) {
  const { section, comments, revisions } = detail;
  const isAccepted = section.id === acceptedAnswerSectionId;
  // Suppress the section heading when it'd just be a stutter of the
  // doc title two rows above. The pills (status / accepted / locked)
  // still render — they carry information the doc-level chrome doesn't.
  const sectionTitle = (section.title || "").trim();
  const docTitleNorm = (docTitle || "").trim();
  const isTitleDuplicate =
    sectionTitle.length > 0 && sectionTitle === docTitleNorm;
  return (
    <div className="max-w-3xl">
      <div className="flex items-baseline gap-3 mb-1 flex-wrap">
        {!isTitleDuplicate && (
          <h2 className="text-2xl font-semibold">{section.title}</h2>
        )}
        <span className={`text-xs px-2 py-0.5 rounded-full ${statusPillClass(section.status)}`}>
          {section.status}
        </span>
        {isAccepted && (
          <span
            className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 font-medium"
            title="The asker accepted this as the answer"
          >
            ✓ Accepted answer
          </span>
        )}
        {section.locked && (
          <span
            className="text-xs px-2 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 font-medium uppercase tracking-wide"
            title="Section locked by owner — only the owner can edit"
          >
            Locked
          </span>
        )}
      </div>
      <VoteAndAcceptBar
        docId={docId}
        section={section}
        myRole={myRole}
        isAnswer={isAnswer}
        isAccepted={isAccepted}
        onMutated={onMutated}
      />
      {section.summary && (
        <div className="text-sm italic text-zinc-500 mb-4">{section.summary}</div>
      )}
      {section.locked && (
        <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-3 italic">
          The owner has locked this section. Edits, claims, and child appends are
          refused for everyone but the doc owner until they unlock it.
        </div>
      )}
      {section.claim && (
        <div className="text-xs text-amber-700 dark:text-amber-400 mb-3">
          ✋ Claimed by {section.claim.party_id.slice(0, 8)}… until {new Date(section.claim.expires_at).toLocaleTimeString()}
        </div>
      )}
      <article className="prose dark:prose-invert prose-zinc max-w-none mb-8">
        {section.body && section.body.trim() ? (
          <CodeAwareBody
            body={stripRedundantLeadingHeading(section.body, [
              docTitle,
              section.title,
            ])}
          />
        ) : (
          <EmptyBodyPlaceholder summary={section.summary} />
        )}
      </article>

      {section.citations.length > 0 && (
        <div className="text-xs text-zinc-500 mb-6">
          {section.citations.length} citation{section.citations.length === 1 ? "" : "s"}
        </div>
      )}

      <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4">
        <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">
          Comments ({comments.length})
        </div>
        {comments.length === 0 ? (
          <div className="text-sm text-zinc-500 mb-3">No comments on this section.</div>
        ) : (
          <ThreadedComments
            comments={comments}
            docId={docId}
            myRole={myRole}
            onResolved={onMutated}
          />
        )}
        {roleAtLeast(myRole, "commenter") && (
          <CommentComposer
            docId={docId}
            sectionId={section.id}
            onPosted={onMutated}
          />
        )}
      </div>

      {revisions.length > 0 && (
        <div className="mt-6 text-xs text-zinc-400">
          {revisions.length} recent change{revisions.length === 1 ? "" : "s"} on this section
        </div>
      )}
    </div>
  );
}

// If the body's first non-blank line is a Markdown h1 that matches the doc
// title or section title (which already render as headings in surrounding
// chrome), strip it. Some agents emit `# <title>` as the first line of body
// content; left in place it renders as a duplicate of the title visible
// above the body.
function stripRedundantLeadingHeading(body: string, titles: string[]): string {
  const normalized = titles
    .map((t) => (t || "").trim().toLowerCase())
    .filter((t) => t.length > 0);
  if (normalized.length === 0) return body;
  const lines = body.split(/\r?\n/);
  let i = 0;
  while (i < lines.length && lines[i].trim() === "") i++;
  if (i >= lines.length) return body;
  const firstLine = lines[i].trim();
  const h1Match = firstLine.match(/^#\s+(.+?)\s*$/);
  if (!h1Match) return body;
  const headingText = h1Match[1].trim().toLowerCase();
  if (!normalized.includes(headingText)) return body;
  let j = i + 1;
  while (j < lines.length && lines[j].trim() === "") j++;
  return [...lines.slice(0, i), ...lines.slice(j)].join("\n");
}

/**
 * Shown in the section body slot when `section.body` is empty. The data
 * isn't broken — the section is in an intermediate "outlined but not
 * drafted" state, which is a legitimate phase of collaborative authoring
 * (especially when an agent has scaffolded an outline before populating
 * bodies). Without this placeholder the body slot rendered as silent
 * whitespace + "No content yet.", which read as a blank page.
 *
 * If a `summary` exists, surface it prominently — it describes what the
 * body will be, which is more useful than just "empty". If there's no
 * summary either, fall back to a neutral explanation.
 */
function EmptyBodyPlaceholder({ summary }: { summary: string | null | undefined }) {
  const hasSummary = !!(summary && summary.trim());
  return (
    <div className="not-prose my-2 rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-900/40 px-4 py-3 text-sm text-zinc-600 dark:text-zinc-400">
      <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500 dark:text-zinc-500 mb-2">
        Outline · needs body
      </div>
      {hasSummary ? (
        <>
          <div className="italic text-zinc-700 dark:text-zinc-300">
            {summary}
          </div>
          <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-500">
            The section was outlined with this summary but the full body
            hasn't been written yet. If you're the author, return to your AI
            client and ask it to populate this section.
          </div>
        </>
      ) : (
        <div className="italic">
          This section was created without body content yet. If you're the
          author, return to your AI client and ask it to populate this
          section.
        </div>
      )}
    </div>
  );
}

/**
 * Stack-Overflow-style vote bar + accept toggle (Phase 13).
 *
 * - Up/down arrows + cached score. Both buttons require the COMMENTER role
 *   so anonymous viewers see the score but can't vote. Mutual exclusion is
 *   enforced server-side; the local optimistic state mirrors that.
 * - The "Accept this answer" toggle only renders when the caller is the
 *   owner AND the section is a direct child of `outline_root` (= an answer,
 *   not the question itself or a deeper descendant).
 */
function VoteAndAcceptBar({
  docId,
  section,
  myRole,
  isAnswer,
  isAccepted,
  onMutated,
}: {
  docId: string;
  section: SectionDetail;
  myRole: DocumentRole;
  isAnswer: boolean;
  isAccepted: boolean;
  onMutated: () => void;
}) {
  const canVote = roleAtLeast(myRole, "commenter");
  const canAccept = myRole === "owner" && isAnswer;
  const upvoteMut = useMutation({
    mutationFn: () => docsApi.endorseSection(docId, section.id),
    onSuccess: onMutated,
  });
  const downvoteMut = useMutation({
    mutationFn: () => docsApi.downvoteSection(docId, section.id),
    onSuccess: onMutated,
  });
  const acceptMut = useMutation<{ ok: true; accepted_answer_section_id: string | null }, Error, void>({
    mutationFn: async () =>
      isAccepted
        ? await docsApi.unacceptAnswer(docId)
        : await docsApi.acceptAnswer(docId, section.id),
    onSuccess: onMutated,
  });
  if (!canVote && !canAccept) return null;
  return (
    <div className="flex items-center gap-2 mb-4">
      {canVote && (
        <div
          className="inline-flex items-center gap-1 text-sm border border-zinc-200 dark:border-zinc-800 rounded-md px-1 py-0.5"
          aria-label="Vote on this section"
        >
          <button
            type="button"
            onClick={() => upvoteMut.mutate()}
            disabled={upvoteMut.isPending || downvoteMut.isPending}
            className="px-2 py-1 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50"
            title="Upvote"
          >
            ▲
          </button>
          <span
            className={`min-w-[2ch] text-center font-mono font-semibold ${
              section.score > 0
                ? "text-amber-700 dark:text-amber-400"
                : section.score < 0
                ? "text-zinc-500"
                : "text-zinc-600 dark:text-zinc-300"
            }`}
            title={`${section.endorsements?.length ?? 0} upvotes, ${section.downvoters?.length ?? 0} downvotes`}
          >
            {section.score}
          </span>
          <button
            type="button"
            onClick={() => downvoteMut.mutate()}
            disabled={upvoteMut.isPending || downvoteMut.isPending}
            className="px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
            title="Downvote"
          >
            ▼
          </button>
        </div>
      )}
      {canAccept && (
        <button
          type="button"
          onClick={() => acceptMut.mutate()}
          disabled={acceptMut.isPending}
          className={`text-xs px-3 py-1.5 rounded-md font-medium transition disabled:opacity-50 ${
            isAccepted
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : "border border-emerald-600 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
          }`}
          title={
            isAccepted
              ? "Click to unaccept this answer"
              : "Click to mark this as the accepted answer"
          }
        >
          {isAccepted ? "✓ Accepted (click to revoke)" : "Accept this answer"}
        </button>
      )}
    </div>
  );
}

function ThreadedComments({
  comments,
  docId,
  myRole,
  onResolved,
}: {
  comments: CommentRow[];
  docId: string;
  myRole: DocumentRole;
  onResolved: () => void;
}) {
  // Build a tree: roots first (no reply_to OR parent missing from this list),
  // then their replies grouped under them. Each level is sorted by timestamp.
  const byId = new Map(comments.map((c) => [c.id, c]));
  const childrenByParent = new Map<string, CommentRow[]>();
  const roots: CommentRow[] = [];
  for (const c of comments) {
    if (c.reply_to && byId.has(c.reply_to)) {
      const arr = childrenByParent.get(c.reply_to) ?? [];
      arr.push(c);
      childrenByParent.set(c.reply_to, arr);
    } else {
      // Either a true root, or a reply whose parent is on a different section
      // / has been deleted — render as a root so it doesn't get lost.
      roots.push(c);
    }
  }
  const byTime = (a: CommentRow, b: CommentRow) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0);
  roots.sort(byTime);
  for (const arr of childrenByParent.values()) arr.sort(byTime);

  return (
    <ul className="space-y-4 mb-3">
      {roots.map((root) => {
        const replies = childrenByParent.get(root.id) ?? [];
        return (
          <li key={root.id}>
            <CommentRowItem
              comment={root}
              docId={docId}
              myRole={myRole}
              onResolved={onResolved}
            />
            {replies.length > 0 && (
              <ul className="mt-2 ml-3 sm:ml-5 pl-3 sm:pl-4 border-l-2 border-zinc-200 dark:border-zinc-800 space-y-3">
                {replies.map((r) => (
                  <li key={r.id}>
                    <CommentRowItem
                      comment={r}
                      docId={docId}
                      myRole={myRole}
                      onResolved={onResolved}
                      isReply
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CommentRowItem({
  comment,
  docId,
  myRole,
  onResolved,
  isReply = false,
}: {
  comment: CommentRow;
  docId: string;
  myRole: DocumentRole;
  onResolved: () => void;
  isReply?: boolean;
}) {
  const resolveMut = useMutation({
    mutationFn: () => docsApi.resolveComment(docId, comment.id),
    onSuccess: onResolved,
  });
  const canResolve = !comment.resolved && roleAtLeast(myRole, "commenter");
  // Note: this component is used both inside <ul> at the root level and inside
  // <li> for replies. We render only the comment content here (no <li> wrapper)
  // so the parent ThreadedComments owns the list semantics.
  const isModel = comment.author_kind === "model";
  return (
    <div
      className={`text-sm ${isReply ? "text-zinc-700 dark:text-zinc-300" : ""} ${
        isModel
          ? "border-l-2 border-violet-300 dark:border-violet-700 pl-2 -ml-2"
          : ""
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500 mb-0.5">
        {isReply && (
          <span className="text-zinc-400 dark:text-zinc-600 font-medium">Reply</span>
        )}
        {isModel ? (
          // Integrated-model review (Phase 12.9). Render a distinct badge so
          // the reader knows this is a platform-hosted Claude/GPT call, not
          // the user's own connector-driven agent.
          <span
            className="inline-flex items-center gap-1 rounded-md bg-violet-100 dark:bg-violet-950/50 text-violet-800 dark:text-violet-300 px-1.5 py-0.5 font-medium"
            title="Reviewed by an integrated model — credits were deducted from the requester's balance."
          >
            🧠 {comment.model_name ?? "Integrated model"}
          </span>
        ) : (
          <>
            <span title={comment.author_party_id}>
              {comment.author_party_id.slice(0, 8)}…
            </span>
            <span className="text-zinc-300 dark:text-zinc-700">·</span>
            <span>{comment.author_kind}</span>
          </>
        )}
        <span className="text-zinc-300 dark:text-zinc-700">·</span>
        <span>{new Date(comment.ts).toLocaleString()}</span>
        {comment.resolved ? (
          <span className="sm:ml-auto text-emerald-600 dark:text-emerald-400">resolved</span>
        ) : canResolve ? (
          <button
            type="button"
            onClick={() => resolveMut.mutate()}
            disabled={resolveMut.isPending}
            className="sm:ml-auto text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 disabled:opacity-50"
          >
            {resolveMut.isPending ? "…" : "Resolve"}
          </button>
        ) : null}
      </div>
      <div className={`${comment.resolved ? "text-zinc-500 line-through" : ""} whitespace-pre-wrap`}>
        {comment.body}
      </div>
    </div>
  );
}

function CommentComposer({
  docId,
  sectionId,
  onPosted,
}: {
  docId: string;
  sectionId: string;
  onPosted: () => void;
}) {
  const [body, setBody] = useState("");
  const postMut = useMutation({
    mutationFn: () => docsApi.postComment(docId, { section_id: sectionId, body: body.trim() }),
    onSuccess: () => {
      setBody("");
      onPosted();
    },
  });
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    postMut.mutate();
  };
  return (
    <form onSubmit={handleSubmit} className="mt-2">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Leave a comment for the agents…"
        rows={2}
        className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-2 text-sm"
      />
      {postMut.error && (
        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
          {apiErrText(postMut.error) ?? String(postMut.error)}
        </div>
      )}
      <div className="mt-1 flex justify-end">
        <button
          type="submit"
          disabled={!body.trim() || postMut.isPending}
          className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1 disabled:opacity-50"
        >
          {postMut.isPending ? "Posting…" : "Post comment"}
        </button>
      </div>
    </form>
  );
}

function statusPillClass(status: string): string {
  switch (status) {
    case "drafted":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
    case "drafting":
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200";
    case "reviewed":
      return "bg-emerald-200 text-emerald-900 dark:bg-emerald-800/60 dark:text-emerald-100";
    case "finalized":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    default:
      return "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300";
  }
}

function ActivityFeed({ revisions }: { revisions: RevisionRow[] }) {
  if (revisions.length === 0) {
    return <div className="text-sm text-zinc-500 px-1 py-2">No activity yet.</div>;
  }
  // Newest first
  const sorted = [...revisions].sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0));
  return (
    <ul className="space-y-1">
      {sorted.map((r) => (
        <li key={r.id} className="text-xs px-1 py-1 border-b border-zinc-100 dark:border-zinc-900 last:border-b-0">
          <div className="flex items-baseline gap-1.5">
            <span className="text-zinc-500">{new Date(r.ts).toLocaleTimeString()}</span>
            <span className="font-mono text-zinc-700 dark:text-zinc-300">{r.op}</span>
            <span className="text-zinc-500 truncate" title={r.party_id}>
              {r.party_id.slice(0, 8)}…
            </span>
          </div>
          {r.rationale && (
            <div className="text-zinc-500 truncate" title={r.rationale}>
              {r.rationale}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function ErrorScreen({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-sm text-zinc-500 mb-4">{body}</p>
        <Link to="/" className="text-sm text-zinc-700 dark:text-zinc-300 underline">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}

// ---- DownloadMenu: Markdown / Word download for any visible doc ----------
//
// Anchored `<details>` element — no JS state needed, native open/close,
// keyboard-accessible. The browser handles `Content-Disposition: attachment`
// from the server, so the <a> tags below trigger save dialogs rather than
// navigating away. The native `download` attribute is a no-op when the
// server already sets Content-Disposition; harmless either way.

function DownloadMenu({ docId }: { docId: string }) {
  return (
    <details className="relative group">
      <summary
        className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-2.5 py-1 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900 list-none flex-shrink-0"
        title="Download this document"
      >
        Download <span className="text-zinc-400">▾</span>
      </summary>
      <div className="absolute right-0 mt-1 z-20 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-md min-w-[10rem] py-1">
        <a
          href={docsApi.downloadUrl(docId, "md")}
          className="block px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-900"
          download
        >
          Markdown <span className="text-zinc-400">.md</span>
        </a>
        <a
          href={docsApi.downloadUrl(docId, "docx")}
          className="block px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-900"
          download
        >
          Word <span className="text-zinc-400">.docx</span>
        </a>
      </div>
    </details>
  );
}

// ---- ContextualActions: Submit / Approve / Send back ---------------------

function ContextualActions(props: {
  status: string;
  isMaintainer: boolean;
  myRole: DocumentRole;
  submitting: boolean;
  approving: boolean;
  revoking: boolean;
  submitError: string | null;
  approveError: string | null;
  revokeError: string | null;
  onSubmit: (force: boolean) => void;
  onApprove: () => void;
  onAskRevoke: () => void;
  publishedUrl: string | null;
}) {
  const {
    status, isMaintainer,
    submitting, approving,
    submitError, approveError, revokeError,
    onSubmit, onApprove, onAskRevoke,
  } = props;

  // Status → action visibility
  if (status === "outlining" || status === "drafting") {
    if (!isMaintainer) return null;
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSubmit(false)}
          disabled={submitting}
          className="text-xs rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-2.5 py-1 disabled:opacity-50"
          title="Submit for human approval"
        >
          {submitting ? "Submitting…" : "Submit for approval"}
        </button>
        {submitError && (
          <button
            type="button"
            onClick={() => onSubmit(true)}
            disabled={submitting}
            className="text-xs text-amber-700 dark:text-amber-400 underline"
            title={submitError}
          >
            Force submit
          </button>
        )}
        <span className="text-zinc-300 dark:text-zinc-700">·</span>
      </div>
    );
  }

  if (status === "awaiting_humans") {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={approving}
          className="text-xs rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 disabled:opacity-50"
        >
          {approving ? "Approving…" : "Approve"}
        </button>
        {isMaintainer && (
          <button
            type="button"
            onClick={onAskRevoke}
            disabled={props.revoking}
            className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-2.5 py-1 hover:bg-zinc-50 dark:hover:bg-zinc-900 disabled:opacity-50"
          >
            {props.revoking ? "Sending…" : "Send back"}
          </button>
        )}
        {(approveError || revokeError) && (
          <span className="text-xs text-red-600 dark:text-red-400 max-w-xs truncate" title={approveError ?? revokeError ?? ""}>
            {approveError ?? revokeError}
          </span>
        )}
        <span className="text-zinc-300 dark:text-zinc-700">·</span>
      </div>
    );
  }

  if (status === "published") {
    if (props.publishedUrl) {
      return (
        <a
          href={props.publishedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 flex-shrink-0"
        >
          ✓ View published
        </a>
      );
    }
    return (
      <span className="text-xs text-emerald-700 dark:text-emerald-400">
        ✓ Published
      </span>
    );
  }
  return null;
}

// ---- Send-back rationale prompt -------------------------------------------

function RevokePrompt(props: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (rationale: string) => void;
}) {
  const [text, setText] = useState("");
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    props.onConfirm(text.trim());
  };
  return (
    <div className="border-b border-zinc-200 dark:border-zinc-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
            Send this back to drafting
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            rows={2}
            placeholder="Why are you sending this back? The agents will see this comment."
            className="w-full rounded-md border border-amber-300 dark:border-amber-800 bg-white dark:bg-zinc-900 p-2 text-sm"
          />
        </div>
        <div className="flex sm:flex-col gap-2 flex-shrink-0">
          <button
            type="submit"
            disabled={!text.trim() || props.busy}
            className="text-xs rounded-md bg-amber-700 dark:bg-amber-600 text-white px-3 py-1.5 disabled:opacity-50 flex-1 sm:flex-none"
          >
            {props.busy ? "Sending…" : "Send back"}
          </button>
          <button
            type="button"
            onClick={props.onCancel}
            className="text-xs text-zinc-600 dark:text-zinc-400 hover:underline"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function apiErrText(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | string | null;
    if (typeof body === "string") return body;
    return body?.error ?? `HTTP ${err.status}`;
  }
  return String(err);
}
